import { logError, logInfo } from "@/Utils/logger";
export class ImagePreloader {
  private static instance: ImagePreloader;
  private static readonly IMAGE_URLS = [
    "/images/hand-green.png",
    "/images/hand-red.png",
    "/images/left-leg-green.png",
    "/images/left-leg-red.png",
    "/images/right-leg-green.png",
    "/images/right-leg-red.png",
    "/images/body-scan-silhouette.png",
    "/images/correct.png",
    "/images/full-body-capture.png",
  ];

  private loadedImages: Map<string, HTMLImageElement> = new Map();
  private loadedCount = 0;
  private callbacks: (() => void)[] = [];
  private allLoaded = false;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // milliseconds
  private retryAttempts: Map<string, number> = new Map();
  private failedImages: Set<string> = new Set();

  private constructor() {
    this.preload();
  }

  public static getInstance(): ImagePreloader {
    if (!ImagePreloader.instance) {
      ImagePreloader.instance = new ImagePreloader();
    }
    return ImagePreloader.instance;
  }

  private preload(): void {
    if (ImagePreloader.IMAGE_URLS.length === 0) {
      this.handleAllImagesLoaded();
      return;
    }

    ImagePreloader.IMAGE_URLS.forEach((imageUrl) => {
      this.loadImageWithRetry(imageUrl);
    });
  }

  private loadImageWithRetry(imageUrl: string): void {
    const img = new Image();
    img.src = imageUrl;

    img.onload = () => {
      this.loadedImages.set(imageUrl, img);
      this.loadedCount++;
      this.retryAttempts.delete(imageUrl); // Clear retry count on success

      if (this.loadedCount === ImagePreloader.IMAGE_URLS.length) {
        this.handleAllImagesLoaded();
      }
    };

    img.onerror = () => {
      const currentRetries = this.retryAttempts.get(imageUrl) || 0;

      if (currentRetries < this.MAX_RETRIES) {
        this.retryAttempts.set(imageUrl, currentRetries + 1);
        logError(
          `Failed to load image: ${imageUrl}. Retrying... (${
            currentRetries + 1
          }/${this.MAX_RETRIES})`
        );

        setTimeout(() => {
          this.loadImageWithRetry(imageUrl);
        }, this.RETRY_DELAY);
      } else {
        logError(
          `Failed to load image after ${this.MAX_RETRIES} retries: ${imageUrl}`
        );
        this.failedImages.add(imageUrl);
        this.handleImageLoadFailure();
      }
    };
  }

  private async handleImageLoadFailure(): Promise<void> {
    // If all images have either loaded or failed after max retries
    const totalProcessed = this.loadedCount + this.failedImages.size;

    if (totalProcessed === ImagePreloader.IMAGE_URLS.length) {
      if (this.failedImages.size > 0) {
        await logError(
          `Failed to load ${this.failedImages.size} images after retries. Reloading page...`
        );
        // Reload the page as a last resort
        setTimeout(() => {
          window.location.reload();
        }, 1000); // gives time for logs to be sent (will help debugging)
      } else {
        this.handleAllImagesLoaded();
      }
    }
  }

  public onAllLoaded(callback: () => void): void {
    if (this.allLoaded) {
      callback();
    } else {
      this.callbacks.push(callback);
    }
  }

  public getImage(url: string): HTMLImageElement | undefined {
    return this.loadedImages.get(url);
  }

  /**
   * Retry loading all failed images. This can be called manually if needed.
   */
  public retryFailedImages(): void {
    if (this.failedImages.size === 0) return;

    logInfo(`Retrying ${this.failedImages.size} failed images...`);
    const failedUrls = Array.from(this.failedImages);
    this.failedImages.clear();
    this.retryAttempts.clear();

    failedUrls.forEach((url) => {
      this.loadImageWithRetry(url);
    });
  }

  /**
   * Reset the preloader state and reload all images
   */
  public reset(): void {
    this.loadedImages.clear();
    this.loadedCount = 0;
    this.allLoaded = false;
    this.retryAttempts.clear();
    this.failedImages.clear();
    this.callbacks = [];
    this.preload();
  }

  private handleAllImagesLoaded(): void {
    this.allLoaded = true;
    this.callbacks.forEach((cb) => cb());
    this.callbacks = []; // Clear callbacks after execution
  }
}
